import { getRefundableUntilAndIsRefundableFromCancellationPolicy } from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import {
  CANCELLATION_POLICIES,
  PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES,
} from 'constants/cancellationPolicies'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'

export type CompactCancellationInclusionKind = 'free' | 'flexible'

type CompactCancellationInclusionArgs = {
  cancellationPolicyType?: App.CancellationPolicyType
  checkInDate?: string
  offerType: App.OfferType
  timezoneOffset?: number
}

/**
 * Mirrors `getCancellationPolicyText` highlight == "Free cancellation".
 * Prior-to-check-in / flexible policies use "Flexible cancellation" and must not
 * be labelled Free cancellation in the compact inclusion row.
 */
export function shouldShowCompactFreeCancellationInclusion({
  cancellationPolicyType,
  checkInDate,
  offerType,
  timezoneOffset,
}: CompactCancellationInclusionArgs): boolean {
  if (!cancellationPolicyType || cancellationPolicyType === CANCELLATION_POLICIES.NON_REFUNDABLE) {
    return false
  }

  const { isRefundable } = getRefundableUntilAndIsRefundableFromCancellationPolicy(
    cancellationPolicyType,
    checkInDate || '',
    timezoneOffset || 0,
  )

  if (!isRefundable) {
    return false
  }

  if (offerType === OFFER_TYPE_ALWAYS_ON) {
    return true
  }

  return cancellationPolicyType === CANCELLATION_POLICIES.POST_PURCHASE_SEVEN_DAYS
}

/**
 * Compact mosaic Flexible row. Mutually exclusive with Free cancellation:
 * Always On refundable policies are Free, not Flexible.
 */
export function shouldShowCompactFlexibleCancellationInclusion({
  cancellationPolicyType,
  checkInDate,
  offerType,
  timezoneOffset,
}: CompactCancellationInclusionArgs): boolean {
  if (shouldShowCompactFreeCancellationInclusion({
    cancellationPolicyType,
    checkInDate,
    offerType,
    timezoneOffset,
  })) {
    return false
  }

  if (!cancellationPolicyType || !PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES.includes(cancellationPolicyType)) {
    return false
  }

  const { isRefundable } = getRefundableUntilAndIsRefundableFromCancellationPolicy(
    cancellationPolicyType,
    checkInDate || '',
    timezoneOffset || 0,
  )

  return isRefundable
}

export function getCompactCancellationInclusionKind(
  args: CompactCancellationInclusionArgs,
): CompactCancellationInclusionKind | undefined {
  if (shouldShowCompactFreeCancellationInclusion(args)) return 'free'
  if (shouldShowCompactFlexibleCancellationInclusion(args)) return 'flexible'
  return undefined
}
